import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ExportImportStatus,
  IExportData,
  IExportDTO,
} from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecords.models";
import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";
import {
  exportAbsenceData,
  fetchExportStatus,
} from "src/Pages/Company/Absences/api";
import { downloadExportFile } from "./useDownloadFile";

export const useExportAbsenceData = () => {
  const queryClient = useQueryClient();

  const exportMutation = useMutation({
    mutationFn: (params: IExportDTO) => exportAbsenceData(params),
    onSuccess: (data) => {
      queryClient.setQueryData(["exportStatus", data.exportId], data);
    },
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd podczas eksportu danych."),
        "error",
        "top-right",
        7000
      );
    },
  });

  const downloadMutation = useMutation({
    mutationFn: (exportId: string) => downloadExportFile(exportId),
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd podczas eksportu danych."),
        "error",
        "top-right",
        7000
      );
    },
  });

  const exportStatusQuery = useQuery({
    queryKey: ["exportStatus", exportMutation.data?.exportId],
    queryFn: async () => {
      const status = await fetchExportStatus(exportMutation.data!.exportId);
      if (status.status === ExportImportStatus.SUCCEEDED) {
        downloadMutation.mutate(exportMutation.data!.exportId);
      }
      return status;
    },
    enabled: !!exportMutation.data?.exportId,
    refetchInterval: (query) =>
      query.state.data?.status !== ExportImportStatus.SUCCEEDED ? 500 : false,
    select: (data: IExportData) => data,
  });

  return {
    exportDataMutation: exportMutation.mutate,
    isExporting: exportMutation.isPending,
    exportError: exportMutation.error,
    exportStatus: exportStatusQuery.data,
    isPolling: exportStatusQuery.isLoading,
    pollingError: exportStatusQuery.error,
    isDownloading: downloadMutation.isPending,
    downloadError: downloadMutation.error,
    hasDownloadSucceeded: downloadMutation.isSuccess,
  };
};
